<template>
    <div v-if="accounts && accounts.length > 0">
        <div class="w-full">
            <table class="w-full setting-group py-2">
                <tbody>
                    <tr class="bank-row text-sm">
                        <th class="col-span-2">Navn</th>
                        <th>Bank</th>
                        <th>Kontonummer</th>
                        <th>IBAN</th>
                        <th>Type</th>
                        <th class="md:max-w-40 truncate overflow-hidden max-w-28">Bankintegrasjon</th>
                        <th>KID</th>
                        <th class="md:max-w-40 truncate overflow-hidden max-w-32">Regnskapskonto</th>
                        <th>Standard inntektskonto</th>
                        <th>Standard betalingskonto</th>
                    </tr>
                    <tr
                        v-for="account in accounts"
                        :key="account.uuid"
                        ref="rowElement"
                        class="bank-row text-sm"
                    >
                        <td>
                            <account-name-input :account="account" />
                        </td>
                        <td>{{ banks.find(b => b.bic === account.bic)?.name ?? account.bic }}</td>
                        <td class="text-nowrap">{{ formatBankAccountNo(account.bban) }}</td>
                        <td>{{ account.iban }}</td>
                        <td>{{ account.type }}</td>
                        <td>
                            <i v-if="account.bankUuid" class="i-ph-check block text-primary text-xl"/>
                            <i v-else class="i-ph-x text-error text-xl block"/>
                        </td>
                        <td>
                            <div v-if="account.kidSettings">
                                KundeNo[{{ account.kidSettings.customerNoLength }}]
                                FakturaNo[{{ account.kidSettings.invoiceNoLength }}]
                                Modulus[{{ account.kidSettings.modulus }}]
                                <UButton class="text-primary text-lg i-ph-pencil" @click="() => onboardOcrComponent?.show(account, account.pendingOcr)"/>
                            </div>
                            <div v-else-if="account.pendingOcr">
                                Venter bekreftelse fra bank
                                <!-- TODO simplify or move logic to click handler -->
                                <UButton v-if="!account.pendingOcr.agreementId" variant="ghost" @click="() => checkOcrStatus(account.bankUuid)">
                                    <i class="i-ph-arrows-clockwise"/>
                                </UButton>
                                <UButton v-else variant="ghost" @click="fetchBankAccounts">
                                    <i class="i-ph-arrows-clockwise"/>
                                </UButton>
                            </div>
                            <UButton v-else class="my-2" @click="() => onboardOcrComponent?.show(account)">Ny avtale</UButton>
                        </td>
                        <td>
                            <bank-bookkeeping-account-picker :account="account"/>
                        </td>
                        <td>
                            <input
                                :disabled="!account.bookkeepingAccountNo"
                                type="checkbox"
                                :checked="account.bookkeepingAccountNo != undefined && defaultIncomingPaymentAccount?.value === account.bookkeepingAccountNo"
                                class="toggle toggle-primary"
                                @change="(e) => onDefaultIncomingAccountChange(e, account)"
                            >
                        </td>
                        <td>
                            <input
                                :disabled="!account.bookkeepingAccountNo"
                                type="checkbox"
                                :checked="account.bookkeepingAccountNo != undefined && defaultOutgoingPaymentAccount?.value === account.bookkeepingAccountNo"
                                class="toggle toggle-primary"
                                @change="(e) => onDefaultOutgoingAccountChange(e, account)"
                            >
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <onboard-ocr-popup ref="onboardOcrComponent" @save="init"/>
    </div>
    <div v-else class="setting-group p-2">
        Det er ikke opprettet noen bankkontoer enda.
    </div>
</template>

<script setup lang="ts">
import {useBanks} from '@/bank/banks.service'
import {RichBankAccount} from '@/bank/bank.types'
import {useSetting} from '@/setting/setting.service'
import {bankAccounts, fetchBankAccounts, formatBankAccountNo} from '@/bank/bank-account.service'
import {computed, onMounted, onUnmounted, ref} from 'vue'
import OnboardOcrPopup from '@/bank/OnboardOcrPopup.vue'
import {useAccount, getOcrOnboardingStatus} from '@/bank/account/account.service'
import {ORG_SWITCHED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import BankBookkeepingAccountPicker from '@/bank/BankBookkeepingAccountPicker.vue'
import AccountNameInput from '@/bank/account/AccountNameInput.vue'

const {accounts: contaBankBankAccounts} = useAccount()
const {banks} = useBanks()

const accounts = computed(() => bankAccounts.value.map(ba => ({
    ...ba,
    pendingOcr: contaBankBankAccounts.value.find(cbba => cbba.uuid === ba.bankUuid)?.ocrSettings
})))

const {onEvent, cleanUp} = useEmitter()

const onboardOcrComponent = ref<InstanceType<typeof OnboardOcrPopup> | null>(null)

const {setting: defaultIncomingPaymentAccount} = useSetting<string | null>('DEFAULT_INCOMING_PAYMENT_ACCOUNT')
const {setting: defaultOutgoingPaymentAccount} = useSetting<string | null>('DEFAULT_OUTGOING_PAYMENT_ACCOUNT')

const rowElement = ref<HTMLElement | null>(null)

const onDefaultIncomingAccountChange = (event: Event, account: RichBankAccount) => {
    if (!account?.bookkeepingAccountNo) return
    const checked = (event.target as HTMLInputElement).checked
    if (!checked) {
        defaultIncomingPaymentAccount.value.value = null
    } else {
        defaultIncomingPaymentAccount.value.value = account.bookkeepingAccountNo
    }
}

const onDefaultOutgoingAccountChange = (event: Event, account: RichBankAccount) => {
    if (!account?.bookkeepingAccountNo) return
    const checked = (event.target as HTMLInputElement).checked
    if (!checked) {
        defaultOutgoingPaymentAccount.value.value = null
    } else {
        defaultOutgoingPaymentAccount.value.value = account.bookkeepingAccountNo
    }
}

const init = async () => {
    // needed for on save kid agreement, results in egregious over fetching, that is however the norm so far, refactor those who dare
    await fetchBankAccounts()
}

const checkOcrStatus = async (accountUuid: string | undefined) => {
    if (!accountUuid) return
    const status = await getOcrOnboardingStatus(accountUuid)
    if (status?.agreementId) {
        // refresh
        await init()
    }
}

onMounted(async () => {
    await init()
    onEvent(ORG_SWITCHED, init)
})

onUnmounted(cleanUp)
</script>


<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.grid-header {
    @apply border-b-2 dark:border-gray-300/30;

    div {
        @apply px-4 font-bold;
    }
}

table {
    th {
        @apply px-2;
    }

    td {
        @apply p-2;
    }

    tr {
        @apply border-b dark:border-gray-400/30 text-left;

        &.bank-row {
            @apply hover:bg-base-200;
        }
    }
}

.grid-row {
    @apply border-b dark:border-gray-400/30;

    & > div {
        @apply px-4;
    }
}
</style>